import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import FunnelChart from '../../../components/founder-dashboard/FunnelChart';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';

const PG_STAGES = {
  pg_started: { label: 'Started Wizard', tip: 'User navigated to the personal group creation screen.' },
  pg_created: { label: 'Group Created', tip: 'User named their group and it was created successfully.' },
  pg_invite_sent: { label: 'Invite Sent', tip: 'User sent at least one invite (email or share sheet).' },
  pg_completed: { label: 'Wizard Completed', tip: 'User finished the wizard and entered the dashboard.' },
};

const PG_COHORT_STAGES = ['pg_started', 'pg_created', 'pg_invite_sent', 'pg_completed'];
const PG_COHORT_LABELS = { pg_started: 'Started', pg_created: 'Created', pg_invite_sent: 'Invited', pg_completed: 'Completed' };

const PRIOR_STAGE_LABELS = {
  signed_up: 'Just Signed Up',
  viewed_options: 'Opened Wizard',
  onboarding_skipped: 'Closed Wizard',
  searched_church: 'Searched Church',
  lead_submitted: 'Submitted Lead',
  notify_me: 'Notify Me',
  join_code_attempted: 'Tried Join Code',
  org_creation_started: 'Started Org Creation',
  unknown: 'Unknown',
};

const PRIOR_STAGE_COLORS = {
  signed_up: 'bg-gray-200',
  viewed_options: 'bg-blue-300',
  onboarding_skipped: 'bg-orange-300',
  searched_church: 'bg-purple-300',
  lead_submitted: 'bg-teal-300',
  notify_me: 'bg-slate-300',
  join_code_attempted: 'bg-indigo-300',
  org_creation_started: 'bg-yellow-300',
  unknown: 'bg-gray-300',
};

function heatmapColor(count, maxCount, mode, pct) {
  if (!count || count === 0) return 'bg-gray-50 text-gray-400';
  const ratio = mode === 'relative'
    ? (pct || 0) / 100
    : count / Math.max(maxCount, 1);
  if (ratio >= 0.75) return 'bg-violet-600 text-white';
  if (ratio >= 0.5) return 'bg-violet-400 text-white';
  if (ratio >= 0.25) return 'bg-violet-200 text-violet-800';
  return 'bg-violet-100 text-violet-700';
}

export default function PersonalGroupFunnelPage() {
  const { currentEnv } = useEnvironment();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cohortMode, setCohortMode] = useState('relative');

  const fetchData = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const [funnelRes, cohortsRes] = await Promise.all([
        founderDashboardAPI.getPersonalGroupFunnel({ refresh }),
        founderDashboardAPI.getPersonalGroupCohorts(),
      ]);
      setFunnel(funnelRes.data.funnel);
      setCohorts(cohortsRes.data.cohorts || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('[PersonalGroupFunnel] Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentEnv]);

  const cohortMaxCount = useMemo(() => {
    if (!cohorts) return 1;
    let max = 0;
    for (const c of cohorts) {
      for (const stage of PG_COHORT_STAGES) {
        const val = c.stages?.[stage] || 0;
        if (val > max) max = val;
      }
    }
    return max || 1;
  }, [cohorts]);

  const funnelStages = funnel
    ? Object.entries(PG_STAGES).map(([name, { label, tip }]) => {
        const stagesData = funnel.stages || {};
        const total = funnel.total || 1;
        const count = Object.entries(stagesData)
          .filter(([s]) => {
            const stagePriority = { pg_started: 1, pg_created: 2, pg_invite_sent: 3, pg_completed: 4 };
            return (stagePriority[s] || 0) >= (stagePriority[name] || 0);
          })
          .reduce((sum, [, c]) => sum + c, 0);
        return {
          name,
          label,
          tooltip: tip,
          count,
          percentage: total > 0 ? Math.round((count / total) * 100) : 0,
        };
      })
    : [];

  const prePgJourney = funnel?.prePgJourney || {};
  const journeyTotal = Object.values(prePgJourney).reduce((sum, c) => sum + c, 0);
  const journeyEntries = Object.entries(prePgJourney)
    .sort(([, a], [, b]) => b - a);

  const inviteMethods = funnel?.inviteMethods || {};
  const shareCount = inviteMethods.personal_group_invite_shared || 0;
  const emailCount = inviteMethods.personal_group_invite_emails_sent || 0;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Couldn't load personal group data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={() => fetchData()}
          className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 min-h-[44px]"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Personal Group Funnel</h3>
          <p className="text-sm text-gray-500 mt-1">
            Conversion through the personal group creation wizard
          </p>
        </div>
        <RefreshIndicator
          onRefresh={() => fetchData(true)}
          loading={loading}
          lastUpdated={lastUpdated}
        />
      </div>

      {/* Weekly Cohorts (top) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Weekly Cohorts</h4>
            <p className="text-sm text-gray-500 mt-1">
              Personal group creators by signup week, showing current wizard stage
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden min-h-[44px]">
            <button
              type="button"
              onClick={() => setCohortMode('absolute')}
              className={`px-4 text-sm font-medium min-h-[44px] ${
                cohortMode === 'absolute'
                  ? 'bg-violet-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              aria-pressed={cohortMode === 'absolute'}
              title="Show absolute counts"
            >
              #
            </button>
            <button
              type="button"
              onClick={() => setCohortMode('relative')}
              className={`px-4 text-sm font-medium min-h-[44px] border-l border-gray-300 ${
                cohortMode === 'relative'
                  ? 'bg-violet-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              aria-pressed={cohortMode === 'relative'}
              title="Show percentage of cohort size"
            >
              %
            </button>
          </div>
        </div>
        {loading ? (
          <div className="h-48 bg-gray-100 rounded animate-pulse" />
        ) : !cohorts || cohorts.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No cohort data yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Cohort</th>
                  <th className="text-right py-3 px-3 font-semibold text-gray-700">Size</th>
                  {PG_COHORT_STAGES.map((stage) => (
                    <th key={stage} className="text-center py-3 px-3 font-semibold text-violet-700">
                      {PG_COHORT_LABELS[stage]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohorts.slice().reverse().map((cohort) => (
                  <tr key={cohort.week} className="border-b border-gray-100">
                    <td className="py-2.5 px-3 text-gray-900 font-medium">{cohort.week}</td>
                    <td className="py-2.5 px-3 text-right font-semibold text-gray-900">{cohort.total}</td>
                    {PG_COHORT_STAGES.map((stage) => {
                      const count = cohort.stages?.[stage] || 0;
                      const pct = cohort.total > 0 ? Math.round((count / cohort.total) * 100) : 0;
                      return (
                        <td
                          key={stage}
                          className={`px-3 py-2.5 text-center text-xs font-medium ${heatmapColor(count, cohortMaxCount, cohortMode, pct)}`}
                        >
                          {count > 0
                            ? cohortMode === 'relative' ? `${pct}%` : count
                            : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* How They Got Here + Invite Methods (middle) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">How They Got Here</h4>
          <p className="text-sm text-gray-500 mb-4">
            Onboarding stage when users entered the PG wizard
          </p>
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[80, 60, 30].map((w, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-32 h-4 bg-gray-200 rounded" />
                  <div className="flex-1 bg-gray-100 rounded-full h-6">
                    <div className="h-6 bg-gray-200 rounded-full" style={{ width: `${w}%` }} />
                  </div>
                  <div className="w-12 h-4 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          ) : journeyEntries.length === 0 ? (
            <p className="text-gray-400 text-center py-6">No journey data yet.</p>
          ) : (
            <div className="space-y-2">
              {journeyEntries.map(([stage, count]) => {
                const pct = journeyTotal > 0 ? Math.round((count / journeyTotal) * 100) : 0;
                const barColor = PRIOR_STAGE_COLORS[stage] || 'bg-gray-300';
                return (
                  <div key={stage} className="flex items-center gap-3">
                    <span className="w-36 text-sm text-gray-600 truncate">
                      {PRIOR_STAGE_LABELS[stage] || stage}
                    </span>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div
                        className={`h-6 rounded-full ${barColor} transition-all duration-500`}
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                    <span className="w-16 text-right text-sm font-medium text-gray-900">
                      {count} ({pct}%)
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Invite Methods</h4>
          <p className="text-sm text-gray-500 mb-4">How PG creators invited people</p>
          {loading ? (
            <div className="grid grid-cols-2 gap-4 animate-pulse">
              <div className="h-24 bg-gray-200 rounded-xl" />
              <div className="h-24 bg-gray-200 rounded-xl" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-violet-700">{shareCount}</p>
                <p className="text-sm text-violet-600 mt-1">Share Sheet</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-indigo-700">{emailCount}</p>
                <p className="text-sm text-indigo-600 mt-1">Email Invites</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PG Wizard Funnel (bottom) */}
      <FunnelChart
        stages={funnelStages}
        title="PG Wizard Funnel"
        colorScheme="violet"
        loading={loading}
        onStageClick={(stage) => navigate(`/onboarding/personal-groups/users/${stage}`)}
      />
    </div>
  );
}
